import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * BÖY SAUCE — ajout au panier depuis un palier de l'escalier.
 *
 * Chaque palier de la fiche produit porte son propre formulaire d'ajout, pour que
 * le client puisse choisir son format ET l'ajouter sans descendre la page ni
 * changer de fiche. Le bouton natif de Shopify est masqué sur les fiches BÖY :
 * il ferait doublon avec celui du palier affiché. Les paiements accélérés, eux,
 * restent rendus par Shopify juste en dessous.
 *
 * Dégradation gracieuse, dans cet ordre :
 *   1. JS chargé, réseau OK  → ajout AJAX, tiroir re-rendu et ouvert ;
 *   2. JS chargé, ajout AJAX en échec → on laisse le formulaire partir en POST
 *      natif vers /cart/add, le client atterrit sur la page panier ;
 *   3. JS non chargé → le formulaire est un vrai formulaire, il fonctionne seul.
 * Dans les trois cas, le client peut acheter. C'est la seule règle qui compte ici.
 *
 * On envoie des quantités ABSOLUES à /cart/update.js plutôt qu'un /cart/add.js,
 * pour réutiliser exactement le contrat déjà éprouvé par boy-upsell.js : même
 * événement, même re-rendu de section, même gestion d'erreur. La quantité
 * courante est relue à chaque clic sur /cart.js, jamais déduite du rendu serveur,
 * sinon deux clics d'affilée sans rechargement enverraient une valeur périmée.
 *
 * Fichier custom BÖY — ne fait pas partie de Savor.
 */
const SECTION_ID = 'cart-drawer-section';

class BoyAdd extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#handleSubmit);
  }

  #track(name) {
    const payload = {
      event: name,
      tier: this.dataset.tier || null,
      bottles: Number(this.dataset.bottles) || null,
      variant: this.dataset.variant || null,
      surface: 'pdp',
    };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    document.dispatchEvent(new CustomEvent(name, { detail: payload }));
  }

  /** Quantité déjà au panier pour cette variante, relue en direct. */
  async #currentQuantity(variantId) {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Lecture du panier impossible');
    const cart = await response.json();
    return (cart.items || [])
      .filter((item) => String(item.variant_id) === String(variantId))
      .reduce((total, item) => total + item.quantity, 0);
  }

  /** Le tiroir ne s'ouvre qu'une fois l'élément personnalisé défini. */
  async #openDrawer() {
    try {
      await customElements.whenDefined('theme-drawer');
      document.getElementById('cart-drawer')?.open?.();
    } catch (error) {
      // Le panier est à jour même si le tiroir refuse de s'ouvrir : on ne casse rien.
      console.warn('[boy-escalier] ouverture du tiroir impossible :', error);
    }
  }

  #handleSubmit = async (event) => {
    const variantId = this.dataset.variant;
    if (!variantId) return; // pas d'identifiant : on laisse le POST natif partir

    event.preventDefault();
    if (this.hasAttribute('data-loading')) return;
    this.setAttribute('data-loading', '');
    this.#track('boy_tier_add');

    const deferred = CartLinesUpdateEvent.createPromise();

    try {
      const quantity = (await this.#currentQuantity(variantId)) + 1;

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [],
          promise: deferred.promise,
        })
      );

      const response = await fetch('/cart/update.js', fetchConfig('json', {
        body: JSON.stringify({
          updates: { [variantId]: quantity },
          sections: SECTION_ID,
          sections_url: window.location.pathname,
        }),
      }));
      const data = await response.json();

      if (!response.ok || data.errors) {
        throw new Error(data.errors || data.description || 'Ajout au panier refusé');
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: { sections: data.sections, source: 'boy-escalier', didError: false },
      });

      await this.#openDrawer();
      this.removeAttribute('data-loading');
    } catch (error) {
      console.warn('[boy-escalier] ajout AJAX impossible, bascule sur le POST natif :', error);
      deferred.reject(error);
      // Le client doit pouvoir acheter : on laisse le vrai formulaire faire le travail.
      this.form?.submit();
    }
  };
}

if (!customElements.get('boy-add')) {
  customElements.define('boy-add', BoyAdd);
}
